import { type FC, type ReactElement } from 'react';
import cx from 'classnames';

import { IconPen16 } from 'src/assets/icons/16x16/IconPen16';
import { IconTrash16 } from 'src/assets/icons/16x16/IconTrash16';
import { IconImage24 } from 'src/assets/icons/24x24/IconImage24';
import { CustomButtonBase } from 'src/components/CustomButtonBase';
import { CustomIconButton } from 'src/components/CustomIconButton';
import { SelectFileArea } from 'src/components/SelectFileArea';

import { useStyles } from './useStyles';

interface Props {
  className?: string;
  classes?: Partial<ReturnType<typeof useStyles>>;
  imageResource?: {
    file?: {
      originalUrl?: string;
    };
  };
  onSelect: () => void;
  onClear?: () => void;
  emptyTitle?: string;
  emptyIcon?: ReactElement;
  buttonLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  staticActionsPosition?: boolean;
  verticalActions?: boolean;
}

export const SelectImageArea: FC<Props> = ({
  className,
  classes,
  imageResource,
  onSelect,
  onClear,
  emptyTitle = 'No image selected',
  emptyIcon,
  buttonLabel = 'Select Image',
  disabled,
  loading,
  staticActionsPosition,
  verticalActions,
}) => {
  const styles = useStyles({ classes });

  if (imageResource?.file?.originalUrl && !loading) {
    return (
      <div
        className={cx(styles.root, styles.rootImage, className, {
          [styles.staticActionsPosition]: staticActionsPosition,
          [styles.verticalActions]: verticalActions,
        })}
      >
        <div className={styles.imageFrame}>
          <CustomButtonBase
            className={styles.imageButton}
            onClick={onSelect}
            disabled={disabled}
          >
            <img
              className={styles.image}
              src={imageResource.file.originalUrl}
              alt=""
            />
          </CustomButtonBase>

          <div className={styles.imageActions}>
            {onClear && (
              <CustomIconButton
                onClick={onClear}
                disabled={disabled}
                variant="outlined"
              >
                <IconTrash16 />
              </CustomIconButton>
            )}

            <CustomIconButton
              onClick={onSelect}
              disabled={disabled}
              variant="outlined"
            >
              <IconPen16 />
            </CustomIconButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SelectFileArea
      className={cx(styles.root, className)}
      classes={classes}
      onSelect={onSelect}
      emptyTitle={emptyTitle}
      emptyIcon={emptyIcon || <IconImage24 />}
      buttonLabel={buttonLabel}
      disabled={disabled}
      loading={loading}
    />
  );
};

SelectImageArea.displayName = 'SelectImageArea';
